import React, { useEffect, useMemo, useRef, useState } from 'react';

export const DEFAULT_FRAME_WIDTH = 100;
export const DEFAULT_FRAME_HEIGHT = 100;
export const DEFAULT_FRAME_COUNT = 5;

export function resolveSpriteSheetLayout(sheet) {
  const fw = sheet?.frameWidth || DEFAULT_FRAME_WIDTH;
  const fh = sheet?.frameHeight || DEFAULT_FRAME_HEIGHT;
  const fc = sheet?.frameCount || DEFAULT_FRAME_COUNT;
  const frameRow = sheet?.frameRow || 0;
  const sheetHeight = sheet?.sheetHeight || fh;
  return { fw, fh, fc, frameRow, sheetHeight };
}

export function spriteSheetStyle(sheet) {
  if (!sheet?.url) return null;
  const { fw, fh, fc, frameRow, sheetHeight } = resolveSpriteSheetLayout(sheet);
  const url = sheet.url.startsWith('/') ? sheet.url : `/${sheet.url}`;
  return {
    backgroundImage: `url(${url})`,
    width: fw,
    height: fh,
    backgroundSize: `${fw * fc}px ${sheetHeight}px`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: `0 ${-frameRow * fh}px`,
  };
}

export function spriteAnimDurationMs(sheet) {
  if (!sheet) return 500;
  const fc = sheet.frameCount || DEFAULT_FRAME_COUNT;
  const fps = sheet.fps || 10;
  return Math.max(200, Math.round((fc / fps) * 1000));
}

const BattleSprite = ({
  sheet,
  playing = true,
  loop = true,
  pulseIntervalMs = 0,
  hideUntilLoaded = false,
  className = '',
  fallbackLabel = '',
  onComplete,
  onImageError,
}) => {
  const [broken, setBroken] = useState(false);
  const [imageReady, setImageReady] = useState(false);
  const [pulseCycle, setPulseCycle] = useState(0);
  const onCompleteRef = useRef(onComplete);
  const pulseTimerRef = useRef(null);
  const { fw, fh, fc, frameRow, sheetHeight } = resolveSpriteSheetLayout(sheet);
  const fps = sheet?.fps || 10;
  const durationSec = fc / fps;
  const usePulse = pulseIntervalMs > 0;
  const effectiveLoop = usePulse ? false : loop;
  const effectivePlaying = playing;

  useEffect(() => {
    onCompleteRef.current = onComplete;
  });

  useEffect(() => {
    setBroken(false);
    setPulseCycle(0);
    setImageReady(false);
  }, [sheet?.url, sheet?.frameRow]);

  useEffect(() => {
    if (!sheet?.url) {
      setImageReady(false);
      return undefined;
    }
    const url = sheet.url.startsWith('/') ? sheet.url : `/${sheet.url}`;
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) setImageReady(true);
    };
    img.onerror = () => {
      if (!cancelled) setImageReady(true);
    };
    img.src = url;
    return () => {
      cancelled = true;
    };
  }, [sheet?.url, sheet?.frameRow]);

  useEffect(() => {
    if (!effectivePlaying || effectiveLoop || !sheet?.url || broken) return undefined;
    const ms = spriteAnimDurationMs(sheet);
    const t = setTimeout(() => {
      if (usePulse) {
        pulseTimerRef.current = setTimeout(() => {
          setPulseCycle((c) => c + 1);
        }, pulseIntervalMs);
      } else {
        onCompleteRef.current?.();
      }
    }, ms);
    return () => clearTimeout(t);
  }, [effectivePlaying, effectiveLoop, sheet, broken, pulseCycle, usePulse, pulseIntervalMs]);

  useEffect(
    () => () => {
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    },
    []
  );

  const url = sheet?.url
    ? (sheet.url.startsWith('/') ? sheet.url : `/${sheet.url}`)
    : '';
  const spriteStyle = useMemo(() => {
    if (!url) return undefined;
    return {
      width: fw,
      height: fh,
      backgroundImage: `url(${url})`,
      backgroundSize: `${fw * fc}px ${sheetHeight}px`,
      backgroundPosition: `0 ${-frameRow * fh}px`,
      '--sprite-frame-count': fc,
      '--sprite-frame-steps': Math.max(1, fc - 1),
      '--sprite-frame-width': `${fw}px`,
      '--sprite-frame-height': `${fh}px`,
      '--sprite-frame-row': frameRow,
      '--sprite-duration': `${durationSec}s`,
    };
  }, [url, fw, fh, fc, sheetHeight, frameRow, durationSec]);

  if (hideUntilLoaded && sheet?.url && !imageReady && !broken) {
    return null;
  }

  if (!sheet?.url || broken) {
    const label = (fallbackLabel || '?').charAt(0).toUpperCase();
    const fallbackAnim = effectivePlaying && effectiveLoop ? 'battle-sprite-fallback--run' : '';
    return (
      <div
        className={`battle-sprite battle-sprite-fallback ${fallbackAnim} ${className}`}
        aria-hidden
      >
        {label}
      </div>
    );
  }

  const animClass = effectivePlaying
    ? effectiveLoop
      ? 'battle-sprite-strip--loop'
      : 'battle-sprite-strip--once'
    : 'battle-sprite-strip--paused';

  return (
    <div
      key={usePulse ? `pulse-${pulseCycle}` : undefined}
      className={`battle-sprite battle-sprite-strip ${animClass} ${className}`}
      style={spriteStyle}
      role="img"
      aria-label={fallbackLabel}
    >
      <img
        src={url}
        alt=""
        className="battle-sprite-probe"
        onError={() => {
          setBroken(true);
          if (onImageError) onImageError();
        }}
      />
    </div>
  );
};

export default BattleSprite;
